const CONTACT_LIMITS = Object.freeze({
  nameMin: 2,
  nameMax: 70,
  emailMax: 254,
  messageMin: 20,
  messageMax: 1500,
});

const URL_PATTERN = /(?:https?:\/\/|www\.|(?:[a-z0-9-]+\.)+(?:com|net|org|io|co|app|dev|xyz|info|biz|me|tv|gg|ly|ai|ar|es|online|site|store|click|link)(?:\b|\/))/i;
const HTML_PATTERN = /<\/?[a-z][^>]*>|javascript\s*:|data\s*:\s*text\/html|on\w+\s*=/i;
const CONTROL_CHARS_PATTERN = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/;
const REPEATED_CHARS_PATTERN = /(.)\1{8,}/i;
const REPEATED_WORD_PATTERN = /\b([a-záéíóúüñ0-9]{3,})\b(?:\s+\1\b){4,}/i;

export const normalizeContactText = (value) => String(value ?? '')
  .normalize('NFKC')
  .replace(/\r\n?/g, '\n')
  .replace(/[ \t]+/g, ' ')
  .trim();

// Normalización exclusiva para moderación. Permite detectar evasiones como
// "p.u.t.a", "p3l0tud0", "hijooo de re mil putasss", etc.
const normalizeForModeration = (value) => normalizeContactText(value)
  .toLowerCase()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/0/g, 'o')
  .replace(/[1!|]/g, 'i')
  .replace(/3/g, 'e')
  .replace(/4|@/g, 'a')
  .replace(/5|\$/g, 's')
  .replace(/7/g, 't')
  .replace(/[._*~|/\\-]+/g, ' ')
  .replace(/([^\s])\1{2,}/g, '$1$1')
  .replace(/[^a-z0-9ñ\s]+/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const hasSuspiciousSymbols = (value) => {
  if (!value) return false;
  const symbols = (value.match(/[^\p{L}\p{N}\s.,;:!?¿¡'"()\-_/+]/gu) || []).length;
  return symbols > Math.max(8, Math.floor(value.length * 0.18));
};

const hasTooMuchUppercase = (value) => {
  const letters = (value.match(/\p{L}/gu) || []);
  if (letters.length < 20) return false;
  const upper = letters.filter((char) => char === char.toUpperCase() && char !== char.toLowerCase()).length;
  return upper / letters.length > 0.8;
};

const OFFENSIVE_PATTERNS = [
  // Insultos argentinos / rioplatenses y castellano general.
  /\b(?:pelotud[oa]s?|bolud[oa]s?|forr[oa]s?|conchud[oa]s?|pajer[oa]s?|cagador(?:a|es)?|garc[ae]s?|ortiv[ao]s?|salame(?:s)?|papafrita(?:s)?|tarad[oa]s?|idiot[ao]s?|imbecil(?:es)?|estupid[oa]s?|inutil(?:es)?|cretin[oa]s?|mediocre(?:s)?|payas[oa]s?|ridicul[oa]s?|basura|mierda)\b/i,
  /\b(?:put[oa]s?|putit[oa]s?|putaz[oa]s?|chot[oa]s?|pij[ao]s?|verga|culo)\b/i,
  /\bhij[oa]\s+de\s+(?:re\s+)?(?:mil\s+)?put[oa]s?\b/i,
  /\bhij[oa]\s+de\s+una?\s+put[oa]\b/i,
  /\b(?:la\s+)?(?:re\s+)?concha\s+de\s+(?:tu|la)\s+madre\b/i,
  /\band(?:a|ate)\s+a\s+(?:la\s+)?mierda\b/i,
  /\band(?:a|ate)\s+a\s+cagar\b/i,
  /\bchupa(?:me)?\s+(?:la\s+)?pija\b/i,
  /\bchupa(?:me)?\s+(?:un\s+)?huevo\b/i,
  /\brompe\s*(?:huevos|pelotas)\b/i,
  /\b(?:sos|son|eres|estan)\s+(?:unos?\s+)?(?:inutil(?:es)?|idiot[ao]s?|pelotud[oa]s?|forr[oa]s?|estupid[oa]s?|mediocre(?:s)?|payas[oa]s?)\b/i,
  /\bno\s+servis\s+para\s+nada\b/i,
  /\bno\s+sirven\s+para\s+nada\b/i,
  /\baprende(?:n)?\s+a\s+(?:programar|trabajar|hacer\s+su\s+trabajo)\b/i,
];

const DISRESPECT_PATTERNS = [
  // Descalificaciones y tono hostil aunque no contengan una puteada explícita.
  /\b(?:que|q)\s+(?:servicio|pagina|web|empresa|equipo)\s+(?:tan\s+)?(?:malo|mala|horrible|pesimo|pesima|patetico|patetica|lamentable|vergonzoso|vergonzosa)\b/i,
  /\b(?:pesimo|pesima|patetico|patetica|lamentable|vergonzoso|vergonzosa)\s+(?:servicio|trabajo|pagina|web|empresa|atencion)\b/i,
  /\b(?:son|sos|eres)\s+(?:un|una|unos|unas)?\s*(?:desastre|verguenza|chiste|fracaso)\b/i,
  /\b(?:esto|su\s+trabajo|tu\s+trabajo)\s+(?:es|parece)\s+(?:un\s+)?(?:desastre|verguenza|chiste|fracaso)\b/i,
  /\b(?:nadie|quien)\s+les\s+(?:enseno|dijo)\s+a\s+(?:programar|trabajar|hacer\s+esto)\b/i,
  /\b(?:deberian|deberias)\s+(?:cerrar|dedicarse\s+a\s+otra\s+cosa|aprender\s+a\s+trabajar)\b/i,
  /\b(?:no\s+tienen|no\s+tenes)\s+idea\s+de\s+(?:nada|lo\s+que\s+hacen)\b/i,
  /\b(?:me\s+da|dan)\s+verguenza\b/i,
];

const SARCASM_PATTERNS = [
  // Frases sarcásticas habituales. Se exigen combinaciones para reducir falsos positivos.
  /\bsi\s+claro\b.*\b(?:genios?|campeones?|profesionales?|excelente|brillante)\b/i,
  /\b(?:genios?|campeones?|profesionales?)\b.*\b(?:eh|no|claro|seguro)\b/i,
  /\bque\s+(?:genios?|profesionales?|maravilla)\b/i,
  /\bexcelente\s+trabajo\b.*\b(?:jaja|ja\s+ja|lol)\b/i,
  /\bbrillante\b.*\b(?:jaja|ja\s+ja|claro)\b/i,
  /\b(?:jaja|jajaja|ja\s+ja)\b.*\b(?:empresa|servicio|profesionales?|genios?)\b/i,
  /\bgracias\s+por\s+nada\b/i,
  /\bque\s+sorpresa\b.*\b(?:otra\s+vez|como\s+siempre)\b/i,
  /\bcomo\s+siempre\b.*\b(?:mal|tarde|nada|fallando)\b/i,
];

const THREAT_PATTERNS = [
  /\bte\s+voy\s+a\s+matar\b/i,
  /\bte\s+mato\b/i,
  /\bte\s+voy\s+a\s+(?:cagar\s+a\s+palos|romper|buscar)\b/i,
  /\bte\s+rompo\s+la\s+cara\b/i,
  /\bvas\s+a\s+morir\b/i,
  /\bte\s+prendo\s+fuego\b/i,
];

const SEXUAL_PATTERNS = [
  /\b(?:porn(?:o|ografia|ografico|ografica)?|xxx|hentai|onlyfans|nudes?|escort(?:s)?|camgirl(?:s)?|prostitucion)\b/i,
  /\bsexo\s+(?:gratis|online)\b/i,
  /\bcontenido\s+adulto\b/i,
  /\b(?:masturbacion|semen|follar)\b/i,
];

const SPAM_PATTERNS = [
  /\b(?:gana|ganar)\s+dinero\s+rapido\b/i,
  /\bdinero\s+facil\b/i,
  /\b(?:inversion|ganancias?)\s+garantizad[ao]s?\b/i,
  /\bcrypto\s+investment\b/i,
  /\b(?:forex|casino|betting|viagra|cialis)\b/i,
  /\bprestamo\s+(?:inmediato|sin\s+requisitos)\b/i,
  /\bseo\s+services?\b/i,
  /\bguest\s+post\b/i,
  /\bbuy\s+(?:followers?|traffic)\b/i,
  /\bclick\s+here\b/i,
];

const matchesAny = (value, patterns) => patterns.some((pattern) => pattern.test(value));

const detectHostileTone = (normalized, rawValue) => {
  // Combinación de tratamiento directo + descalificación/imperativo. No bloquea críticas neutrales.
  const directTarget = /\b(?:vos|tu|ustedes|sos|son|eres|te|les|empresa|equipo)\b/i.test(normalized);
  const contempt = /\b(?:ridiculo|ridicula|pesimo|pesima|patetico|patetica|lamentable|verguenza|desastre|incompetente|incompetentes|mediocre|mediocres|inutil|inutiles)\b/i.test(normalized);
  const hostileImperative = /\b(?:callate|callense|cerra|cierren|aprende|aprendan|dejate|dejense|dedicate|dediquense)\b/i.test(normalized);
  const aggressivePunctuation = /(?:!{3,}|\?{3,}|\?!{2,}|!\?{2,})/.test(rawValue);

  return (directTarget && contempt) || hostileImperative || (contempt && aggressivePunctuation);
};

export const validateContactName = (rawValue) => {
  const value = normalizeContactText(rawValue);
  if (!value) return 'Ingresá tu nombre.';
  if (value.length < CONTACT_LIMITS.nameMin) return 'El nombre es demasiado corto.';
  if (value.length > CONTACT_LIMITS.nameMax) return `El nombre puede tener hasta ${CONTACT_LIMITS.nameMax} caracteres.`;
  if (URL_PATTERN.test(value) || /@/.test(value)) return 'Ingresá un nombre válido, sin enlaces ni emails.';
  if (CONTROL_CHARS_PATTERN.test(value) || HTML_PATTERN.test(value)) return 'El nombre contiene caracteres no permitidos.';
  if (!/^[\p{L}][\p{L}\p{M}' .-]*$/u.test(value)) return 'Usá solo letras y separadores habituales en el nombre.';
  if (REPEATED_CHARS_PATTERN.test(value)) return 'Revisá el nombre ingresado.';
  return '';
};

export const validateContactEmail = (rawValue) => {
  const value = normalizeContactText(rawValue).toLowerCase();
  if (!value) return 'Ingresá tu email.';
  if (value.length > CONTACT_LIMITS.emailMax) return 'El email es demasiado largo.';
  if (/\s|[<>"'`]/.test(value) || value.includes('..')) return 'Ingresá un email válido.';
  const parts = value.split('@');
  if (parts.length !== 2) return 'Ingresá un email válido.';
  const [local, domain] = parts;
  if (!local || local.length > 64 || !domain || domain.length > 253) return 'Ingresá un email válido.';
  if (!/^[a-z0-9.!#$%&*+/=?^_{}|~-]+$/i.test(local)) return 'Ingresá un email válido.';
  if (!/^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/i.test(domain)) return 'Ingresá un email válido.';
  return '';
};

export const validateContactMessage = (rawValue) => {
  const value = normalizeContactText(rawValue);
  if (!value) return 'Contanos brevemente qué necesitás.';
  if (value.length < CONTACT_LIMITS.messageMin) return `Escribí al menos ${CONTACT_LIMITS.messageMin} caracteres para contarnos tu proyecto.`;
  if (value.length > CONTACT_LIMITS.messageMax) return `El mensaje puede tener hasta ${CONTACT_LIMITS.messageMax} caracteres.`;
  if (CONTROL_CHARS_PATTERN.test(value)) return 'El mensaje contiene caracteres no permitidos.';
  if (HTML_PATTERN.test(value)) return 'No se permite código HTML o scripts en el mensaje.';
  if (URL_PATTERN.test(value) || /\b(?:bit\.ly|t\.me|wa\.me|tinyurl\.com)\b/i.test(value)) return 'No se permiten enlaces en el mensaje.';

  const normalized = normalizeForModeration(value);

  if (matchesAny(normalized, THREAT_PATTERNS)) return 'El mensaje contiene amenazas o contenido violento y no puede enviarse.';
  if (matchesAny(normalized, SEXUAL_PATTERNS)) return 'No se permite contenido sexual o pornográfico en el formulario.';
  if (matchesAny(normalized, OFFENSIVE_PATTERNS)) return 'El mensaje contiene lenguaje ofensivo o faltas de respeto. Reformulalo de manera cordial.';
  if (matchesAny(normalized, DISRESPECT_PATTERNS) || detectHostileTone(normalized, value)) return 'El mensaje contiene un tono despectivo o irrespetuoso. Reformulalo de manera cordial.';
  if (matchesAny(normalized, SARCASM_PATTERNS)) return 'El mensaje parece contener sarcasmo o burla. Reformulalo de manera clara y respetuosa.';
  if (matchesAny(normalized, SPAM_PATTERNS)) return 'El mensaje fue detectado como posible spam.';

  if (REPEATED_CHARS_PATTERN.test(value) || REPEATED_WORD_PATTERN.test(value)) return 'El mensaje parece repetitivo o automático.';
  if (hasTooMuchUppercase(value)) return 'Evitá escribir el mensaje principalmente en mayúsculas.';
  if (hasSuspiciousSymbols(value)) return 'El mensaje parece spam. Revisalo antes de enviarlo.';

  return '';
};

export const CONTACT_FORM_LIMITS = CONTACT_LIMITS;
